import { LanguageToProviderTypeMap } from '@common/Fabric/constants';
import { FabricProviderType } from '@common/Fabric/types';
import { FileFormats } from '@common/Transpiler/types';
import { BackEndCodeLanguage, GitCombinedStorageModel } from '@prophecy/graphql-types/enums';
import { getUrlToWorkspaceMappingsQuery, URLToWorkspaceMapping } from '@prophecy/queries/context/edition';
import { removeEmptyParams } from '@prophecy/utils/data';
import { useOnce } from '@prophecy/utils/react/hooks';
import { ResultOf } from 'gql.tada';

import { SQL_PROVIDER } from '../constants/sql';
import { makeGetCall, makeGraphQlCall } from '../data/util';
import { hostUrl } from '../utils/getServerUrl';
import { useAppMetadata } from './appMetadata';
import { DEFAULT_EDITION_SUPPORTED_FABRIC_PROVIDERS, DEFAULT_EDITION_SUPPORTED_SQL_FABRIC_TYPES } from './constants';
import { EditionConfig } from './editionConfigType';

export type { URLToWorkspaceMapping };

async function getEditionConfig() {
  return makeGetCall(hostUrl + '/api/oem/config') as Promise<{
    data: EditionConfig;
    success: boolean;
  }>;
}

const getOEMUrlMapping = async () => {
  return makeGraphQlCall<ResultOf<typeof getUrlToWorkspaceMappingsQuery>>(getUrlToWorkspaceMappingsQuery);
};

export const DefaultEditionConfig: EditionConfig = {
  dataPlaneUrl: hostUrl,
  allowFabricCreation: true,
  supportedLanguages: Object.values(BackEndCodeLanguage),
  supportedGitStorageModels: Object.values(GitCombinedStorageModel),
  supportedFabricProviders: DEFAULT_EDITION_SUPPORTED_FABRIC_PROVIDERS,
  // TODO: Refactor to use a separate project mapping for projects
  supportedSQLFabricTypes: DEFAULT_EDITION_SUPPORTED_SQL_FABRIC_TYPES,
  supportedTranspilers: Object.values(FileFormats),
  teamsWithTranspilerAccess: null,
  isLineageEnabled: true,
  disableTranspiler: false,
  disabledMessage: 'Please contact support team.',
  isSubgraphEnabled: true,
  isModelEnabled: true,
  isSSOEnabled: true,
  isProjectTemplateEnabled: true
};

export async function fetchEditionConfig() {
  const [editionConfigRes, urlMappingRes] = await Promise.all([getEditionConfig(), getOEMUrlMapping()]);
  const editionConfig = editionConfigRes.success
    ? {
        ...DefaultEditionConfig,
        ...removeEmptyParams(editionConfigRes.data || {})
      }
    : DefaultEditionConfig;

  const workspaceUrl = hostUrl.replace(/\/$/, '');
  const mappings = urlMappingRes?.getUrlToWorkspaceMappings || [];
  const matchedMapping = mappings.find((mapping) => mapping.url === workspaceUrl);
  return {
    ...editionConfig,
    dataPlaneUrl: matchedMapping?.dataPlaneUrl || hostUrl
  };
}

export function useEditionConfig() {
  const appMetadata = useAppMetadata();
  return appMetadata.editionConfig;
}

export function useSupportedLanguages() {
  const editionConfig = useEditionConfig();
  return useOnce(() => new Set(editionConfig.supportedLanguages));
}

export function useSupportedGitStorageModels() {
  const editionConfig = useEditionConfig();
  return useOnce(() => new Set(editionConfig.supportedGitStorageModels));
}

export const useSupportedFabricProviderTypes = () => {
  const editionConfig = useEditionConfig();

  return useOnce(() => {
    const supportedTypesByLanguage = new Set<FabricProviderType>();
    editionConfig.supportedLanguages.forEach((language) => {
      LanguageToProviderTypeMap[language].forEach((providerType) => {
        supportedTypesByLanguage.add(providerType);
      });
    });

    const supportedFabricProviderTypes = new Set<FabricProviderType>();
    editionConfig.supportedFabricProviders.forEach((provider) => {
      if (supportedTypesByLanguage.has(provider)) supportedFabricProviderTypes.add(provider);
    });

    return supportedFabricProviderTypes;
  });
};

type FabricTypes<T extends FabricProviderType> = T extends FabricProviderType.SQL ? Set<SQL_PROVIDER> : undefined;
export const useSupportedFabricTypes = <T extends FabricProviderType>(fabricProviderType: T) => {
  const editionConfig = useEditionConfig();

  return useOnce(() => {
    if (fabricProviderType === FabricProviderType.SQL) {
      return new Set(editionConfig.supportedSQLFabricTypes);
    }
  }) as FabricTypes<T>;
};

export const useIsSparkSupported = () => {
  const editionConfig = useEditionConfig();
  return useOnce(() => {
    return [BackEndCodeLanguage.python, BackEndCodeLanguage.scala].some((language) =>
      editionConfig.supportedLanguages.includes(language)
    );
  });
};

export const useIsSQLSupported = () => {
  const editionConfig = useEditionConfig();
  return useOnce(() => {
    return editionConfig.supportedLanguages.includes(BackEndCodeLanguage.sql);
  });
};
